import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CreditsKeys } from "@/features/credits/api/credits";

import {
  cancelSearch as cancelSearchApi,
  confirmSearch,
  createSearch,
  deleteLead as deleteLeadApi,
  getAllLeads,
  getSearchHistory,
  getSearchLeads,
  getSearchStatus,
  getSearchSuggestion,
  LeadsKeys,
  updateLeadStatus as updateLeadStatusApi,
} from "../api/leads";
import type { LeadStatus, SearchStatusResponse } from "../types/leads";

export function useLeads(
  search?: string,
  status?: string,
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  skip?: number,
  limit?: number,
) {
  const { isAuthenticated } = useAuth0();

  return useQuery({
    queryKey: [LeadsKeys.AllLeads, search, status, sortBy, sortOrder, skip, limit],
    queryFn: () => getAllLeads(search, status, sortBy, sortOrder, skip, limit),
    enabled: isAuthenticated,
  });
}

export function useSearchHistory(limit = 20, offset = 0) {
  const { isAuthenticated } = useAuth0();

  return useQuery({
    queryKey: [LeadsKeys.SearchHistory, limit, offset],
    queryFn: () => getSearchHistory(limit, offset),
    enabled: isAuthenticated,
  });
}

export function useSearchStatus(searchId: string | null) {
  const { isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [LeadsKeys.SearchStatus, searchId],
    queryFn: () => getSearchStatus(searchId!),
    enabled: isAuthenticated && !!searchId,
    refetchInterval: (query) => {
      const data = query.state.data as SearchStatusResponse | undefined;
      if (!data) return 3000;
      // Stop polling once terminal or preview done
      if (data.status === "completed" || data.status === "failed" || data.status === "preview_ready") {
        queryClient.invalidateQueries({ queryKey: [LeadsKeys.AllLeads] });
        queryClient.invalidateQueries({ queryKey: [LeadsKeys.SearchHistory] });
        queryClient.invalidateQueries({ queryKey: [LeadsKeys.SearchLeads] });
        queryClient.invalidateQueries({ queryKey: [CreditsKeys.Balance] });
        return false;
      }
      return 3000;
    },
  });
}

export function useSearchLeads(searchId: string | null) {
  const { isAuthenticated } = useAuth0();

  return useQuery({
    queryKey: [LeadsKeys.SearchLeads, searchId],
    queryFn: () => getSearchLeads(searchId!),
    enabled: isAuthenticated && !!searchId,
  });
}

export function useSearchSuggestion() {
  return useMutation({
    mutationFn: () => getSearchSuggestion(),
  });
}

export function useCreateSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (query: string) => createSearch(query),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LeadsKeys.SearchHistory] });
    },
  });
}

export function useConfirmSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      searchId,
      matchLimit,
    }: {
      searchId: string;
      matchLimit: number;
    }) => confirmSearch(searchId, matchLimit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LeadsKeys.SearchHistory] });
    },
  });
}

export function useCancelSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (searchId: string) => cancelSearchApi(searchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LeadsKeys.SearchHistory] });
    },
  });
}

export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      leadId,
      status,
    }: {
      leadId: string;
      status: LeadStatus;
    }) => updateLeadStatusApi(leadId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LeadsKeys.AllLeads] });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => deleteLeadApi(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LeadsKeys.AllLeads] });
    },
  });
}
